import React, { useState, useMemo } from 'react';
import Icon from './Icon';

const MONTHS_PL = ['Sty','Lut','Mar','Kwi','Maj','Cze','Lip','Sie','Wrz','Paź','Lis','Gru'];

const FinancialReport = ({ bookings = [], users = [], reviews = [] }) => {
  const now = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(null); 

  const confirmed = useMemo(() =>
    bookings.filter(b => ['confirmed','completed'].includes(b.status)),
  [bookings]);

  const byMonth = useMemo(() => {
    const map = {};
    for (let m = 0; m < 12; m++) map[m] = { revenue: 0, bookings: 0, products: 0, services: 0 };
    confirmed.forEach(b => {
      const d = new Date(b.createdAt || b.date);
      if (d.getFullYear() !== year) return;
      const m = d.getMonth();
      map[m].revenue  += b.total || 0;
      map[m].bookings += 1;
      map[m].services += b.servicesTotal || 0;
      map[m].products += b.productsTotal || 0;
    });
    return map;
  }, [confirmed, year]);

  const filtered = useMemo(() => {
    if (month === null) return confirmed.filter(b => new Date(b.createdAt || b.date).getFullYear() === year);
    return confirmed.filter(b => {
      const d = new Date(b.createdAt || b.date);
      return d.getFullYear() === year && d.getMonth() === month;
    });
  }, [confirmed, year, month]);

  const totalRev      = filtered.reduce((s,b) => s+(b.total||0), 0);
  const servicesRev   = filtered.reduce((s,b) => s+(b.servicesTotal||0), 0);
  const productsRev   = filtered.reduce((s,b) => s+(b.productsTotal||0), 0);
  const avgBooking    = filtered.length ? Math.round(totalRev / filtered.length) : 0;

  const bySalon = useMemo(() => {
    const map = {};
    filtered.forEach(b => {
      const k = b.salon?.name || 'Nieznany';
      if (!map[k]) map[k] = { revenue: 0, count: 0, city: b.salon?.city || '-' };
      map[k].revenue += b.total || 0;
      map[k].count   += 1;
    });
    return Object.entries(map).sort((a,b) => b[1].revenue - a[1].revenue);
  }, [filtered]);

  const maxRev = bySalon.length ? bySalon[0][1].revenue : 1;

  const exportCSV = () => {
    const rows = [
      ['ID', 'Salon', 'Miasto', 'Data', 'Godzina', 'Klient', 'Usługi', 'Produkty', 'Razem', 'Status'],
      ...filtered.map(b => [
        b.id,
        b.salon?.name || '',
        b.salon?.city || '',
        b.date || '',
        b.time || '',
        b.email || '',
        b.servicesTotal || 0,
        b.productsTotal || 0,
        b.total || 0,
        b.status || '',
      ])
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `hairkus_raport_${year}${month !== null ? '_' + MONTHS_PL[month] : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const years = [now.getFullYear()-1, now.getFullYear()];

  return (
    <div className="report-wrapper">
      {/* Nagłówek */}
      <div className="report-header">
        <div className="report-header-left">
          <div className="report-icon"><Icon name="money" color="white" size="xl" /></div>
          <div>
            <h2>Raport finansowy</h2>
            <p>Analiza przychodów sieci HAIRKUS</p>
          </div>
        </div>
        <div className="report-header-right">
          <div className="report-period-select">
            {years.map(y => (
              <button key={y} className={`report-period-btn ${year === y ? 'active' : ''}`}
                onClick={() => { setYear(y); setMonth(null); }}>{y}</button>
            ))}
          </div>
          <button className="report-export-btn" onClick={exportCSV}>
            <Icon name="download" style={{marginRight:'8px'}} />Eksport CSV
          </button>
        </div>
      </div>

      {/* Filtr miesiąca */}
      <div className="report-months">
        <button className={`report-month-btn ${month === null ? 'active' : ''}`}
          onClick={() => setMonth(null)}>Cały rok</button>
        {MONTHS_PL.map((m, i) => (
          <button key={i} className={`report-month-btn ${month === i ? 'active' : ''}`}
            onClick={() => setMonth(i === month ? null : i)}>
            {m}
            {byMonth[i].revenue > 0 && (
              <span className="month-revenue-dot" style={{
                height: `${Math.max(3, Math.round(byMonth[i].revenue / Math.max(...Object.values(byMonth).map(m => m.revenue), 1) * 20))}px`
              }} />
            )}
          </button>
        ))}
      </div>

      {/* KPI - wskaźniki */}
      <div className="report-kpis">
        {[
          { icon: 'money',    color: '#059669', bg: '#d1fae5', label: 'Łączny przychód',    val: `${totalRev.toLocaleString('pl-PL')} zł` },
          { icon: 'calendar', color: '#1e3c72', bg: '#dbeafe', label: 'Rezerwacje',          val: filtered.length },
          { icon: 'service',  color: '#7c3aed', bg: '#ede9fe', label: 'Przychód z usług',    val: `${servicesRev.toLocaleString('pl-PL')} zł` },
          { icon: 'package',  color: '#d97706', bg: '#fef3c7', label: 'Przychód z produktów',val: `${productsRev.toLocaleString('pl-PL')} zł` },
          { icon: 'star',     color: '#f59e0b', bg: '#fef9c3', label: 'Śr. wartość rezerwacji',val: `${avgBooking} zł` },
          { icon: 'scissors', color: '#0891b2', bg: '#cffafe', label: 'Aktywne salony',      val: [...new Set(filtered.map(b => b.salon?.name).filter(Boolean))].length },
        ].map(k => (
          <div key={k.label} className="report-kpi-card" style={{borderTop: `4px solid ${k.color}`}}>
            <div className="report-kpi-icon" style={{background: k.bg}}>
              <Icon name={k.icon} color={k.color} size="lg" />
            </div>
            <div className="report-kpi-value">{k.val}</div>
            <div className="report-kpi-label">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Wykres miesięczny */}
      {month === null && (
        <div className="report-section">
          <h3 className="report-section-title"><Icon name="stats" style={{marginRight:'8px'}} />Przychody miesięczne {year}</h3>
          <div className="report-chart">
            {MONTHS_PL.map((m, i) => {
              const rev = byMonth[i].revenue;
              const maxMonth = Math.max(...Object.values(byMonth).map(x => x.revenue), 1);
              const pct = Math.round((rev / maxMonth) * 100);
              return (
                <div key={m} className={`report-bar-col ${month === i ? 'active' : ''}`}
                  onClick={() => setMonth(i === month ? null : i)}>
                  <div className="report-bar-val">{rev > 0 ? `${rev} zł` : ''}</div>
                  <div className="report-bar-track">
                    <div className="report-bar-fill" style={{height: `${pct}%`}} />
                  </div>
                  <div className="report-bar-label">{m}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Według salonów */}
      <div className="report-section">
        <h3 className="report-section-title">
          <Icon name="scissors" style={{marginRight:'8px'}} />
          Przychody według salonów
          {month !== null && <span className="report-period-badge">{MONTHS_PL[month]} {year}</span>}
        </h3>
        {bySalon.length === 0 ? (
          <div className="report-empty">
            <Icon name="money" color="#d1d5db" size="2x" />
            <p>Brak zatwierdzonych rezerwacji w wybranym okresie</p>
          </div>
        ) : (
          <div className="report-salon-table">
            <div className="report-table-head">
              <span>Salon</span><span>Rezerwacje</span><span>Udział</span><span>Przychód</span>
            </div>
            {bySalon.map(([name, data]) => (
              <div key={name} className="report-table-row">
                <div className="report-salon-name">
                  <div className="report-salon-dot" style={{background:`hsl(${name.charCodeAt(0)*7%360},60%,50%)`}} />
                  <div>
                    <strong>{name}</strong>
                    <span>{data.city}</span>
                  </div>
                </div>
                <span className="report-cell-center">{data.count}</span>
                <div className="report-bar-mini-wrap">
                  <div className="report-bar-mini" style={{width: `${Math.round(data.revenue/maxRev*100)}%`}} />
                  <span className="report-bar-mini-pct">{Math.round(data.revenue/totalRev*100)}%</span>
                </div>
                <span className="report-cell-right"><strong>{data.revenue.toLocaleString('pl-PL')} zł</strong></span>
              </div>
            ))}
            <div className="report-table-footer">
              <span>ŁĄCZNIE</span>
              <span className="report-cell-center">{filtered.length}</span>
              <span />
              <span className="report-cell-right"><strong>{totalRev.toLocaleString('pl-PL')} zł</strong></span>
            </div>
          </div>
        )}
      </div>

      {/* Ostatnie transakcje */}
      {filtered.length > 0 && (
        <div className="report-section">
          <h3 className="report-section-title"><Icon name="calendar" style={{marginRight:'8px'}} />Ostatnie transakcje</h3>
          <div className="report-transactions">
            {[...filtered].reverse().slice(0, 10).map(b => (
              <div key={b.id} className="report-transaction-row">
                <div className="report-tx-date">
                  <strong>{b.date}</strong>
                  <span>{b.time}</span>
                </div>
                <div className="report-tx-salon">
                  <strong>{b.salon?.name || '-'}</strong>
                  <span>{b.email}</span>
                </div>
                <div className="report-tx-services">
                  {(b.services||[]).map(s => s.name).join(', ') || '-'}
                </div>
                <div className="report-tx-amount">
                  <strong>{b.total} zł</strong>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialReport;
